import { Button, Checkbox } from '@heroui/react'
import type { ConflictAction, ModalComponentProps, MoveConflictModalData } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { FileText, Folder } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export default function MoveConflictModal(props: ModalComponentProps) {
  const { onClose, data } = props
  const modalData = data as MoveConflictModalData
  const { setModalHeader } = useActiveModelStore()

  const [currentConflictIndex, setCurrentConflictIndex] = useState(0)
  const [isApplyToAll, setIsApplyToAll] = useState(false)
  const [resolvedConflicts, setResolvedConflicts] = useState<Map<string, ConflictAction>>(new Map())

  // Custom ESC key handler to ensure modal can always be closed
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()

        // Call completion with null to indicate cancellation
        modalData.onResolutionComplete(null)
        onClose()
      }
    }

    // Add event listener with capture to ensure it's handled before other listeners
    document.addEventListener('keydown', handleEscapeKey, true)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true)
    }
  }, [modalData, onClose])

  // Set modal header when component mounts
  useEffect(() => {
    const currentConflict = modalData?.conflictedFiles?.[currentConflictIndex]

    if (currentConflict) {
      const isFolder = currentConflict.name && !currentConflict.name.includes('.')
      const itemType = isFolder ? 'Folder' : 'File'

      setModalHeader({
        title: `Move ${itemType} Conflict`,
        showCloseButton: true,
      })
    }
  }, [modalData?.conflictedFiles, currentConflictIndex, setModalHeader])

  if (!modalData?.conflictedFiles || modalData.conflictedFiles.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-500 dark:text-gray-400">No conflicts to resolve</p>
      </div>
    )
  }

  const currentConflict = modalData.conflictedFiles[currentConflictIndex]
  const remainingCount = modalData.conflictedFiles.length - currentConflictIndex - 1
  const totalConflicts = modalData.conflictedFiles.length

  // Handle user action (Skip, Replace, Cancel)
  const handleAction = (action: ConflictAction) => {
    if (action === 'cancel') {
      // User cancelled - call completion with null to indicate cancellation
      modalData.onResolutionComplete(null)
      onClose()

      return
    }

    const newResolvedConflicts = new Map(resolvedConflicts)

    if (isApplyToAll) {
      // Apply action to all remaining conflicts
      for (let i = currentConflictIndex; i < modalData.conflictedFiles.length; i++) {
        const conflict = modalData.conflictedFiles[i]

        newResolvedConflicts.set(conflict.id, action)
      }
      // Complete resolution immediately
      completeResolution(newResolvedConflicts)
    } else {
      // Apply action to current conflict only
      newResolvedConflicts.set(currentConflict.id, action)
      setResolvedConflicts(newResolvedConflicts)

      // Move to next conflict or complete if done
      if (currentConflictIndex < modalData.conflictedFiles.length - 1) {
        setCurrentConflictIndex(currentConflictIndex + 1)
      } else {
        // All conflicts resolved
        completeResolution(newResolvedConflicts)
      }
    }
  }

  // Complete the resolution process
  const completeResolution = (finalResolvedConflicts: Map<string, ConflictAction>) => {
    const originalFileIds = modalData.originalRequest.fileIds
    const conflictedFileIds = modalData.conflictedFiles.map(f => f.id)

    // Files to include in final request:
    // 1. Non-conflicted files (original files not in conflicted list)
    // 2. Conflicted files marked as "replace"
    const nonConflictedFiles = originalFileIds.filter(id => !conflictedFileIds.includes(id))
    const replacedFiles = modalData.conflictedFiles
      .filter(conflict => finalResolvedConflicts.get(conflict.id) === 'replace')
      .map(conflict => conflict.id)

    const finalFileIds = [...nonConflictedFiles, ...replacedFiles]

    // If no files to move, return null
    if (finalFileIds.length === 0) {
      toast.info('Move operation cancelled - all files were skipped')
      modalData.onResolutionComplete(null)
      onClose()

      return
    }

    // Determine if we need force=true (if any files were marked for replace)
    const needsForce = replacedFiles.length > 0

    const finalRequest = {
      ...modalData.originalRequest,
      fileIds: finalFileIds,
      force: needsForce,
    }

    modalData.onResolutionComplete(finalRequest)
    onClose()
  }

  // Determine if it's a file or folder based on common conventions
  const isFolder = currentConflict.name && !currentConflict.name.includes('.')
  const Icon = isFolder ? Folder : FileText

  return (
    <div className="p-3 flex flex-col gap-4 max-w-2xl">
      {/* Conflict Details */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed ">
              The{' '}
              <span className="text-gray-900 dark:text-white font-semibold break-words">
                {currentConflict.name}
              </span>{' '}
              {isFolder ? 'folder' : 'file'} already exists in the destination.
              <br />
              Do you want to replace it or skip this item?
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-sm mt-2">
              <strong className="text-gray-900 dark:text-white">Note:</strong> Replacing will move
              the existing {isFolder ? 'folder' : 'file'} to recycle bin.
            </p>
          </div>
        </div>
      </div>

      {/* Apply to All Checkbox */}
      {remainingCount > 0 && (
        <div className="">
          <Checkbox
            isSelected={isApplyToAll}
            size="sm"
            classNames={{
              base: 'text-blue-700 dark:text-blue-400',
              label: 'text-sm font-medium text-blue-800 dark:text-blue-300',
            }}
            onValueChange={setIsApplyToAll}
          >
            Apply this action to all remaining conflicts ({remainingCount} remaining)
          </Checkbox>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end ">
        <Button
          className="w-full sm:w-auto min-w-[100px] h-10"
          color="default"
          size="sm"
          variant="bordered"
          onPress={() => handleAction('cancel')}
        >
          Cancel
        </Button>

        <Button
          className="w-full sm:w-auto min-w-[100px] h-10"
          color="default"
          size="sm"
          variant="bordered"
          onPress={() => handleAction('skip')}
        >
          Skip
        </Button>

        <Button
          className="w-full sm:w-auto min-w-[100px] h-10"
          color="primary"
          size="sm"
          variant="solid"
          onPress={() => handleAction('replace')}
        >
          Replace
        </Button>
      </div>

      {/* Progress indicator */}
      {totalConflicts > 1 && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-3">
            <span className="font-medium">
              Conflict {currentConflictIndex + 1} of {totalConflicts}
            </span>
            <span>{remainingCount} remaining</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${((currentConflictIndex + 1) / totalConflicts) * 100}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
